import React from "react";

import { Root } from "@radix-ui/react-accordion";
import { FormProvider, useForm } from "react-hook-form";

import { useConnection } from "src/api/connectApi/queries";
import { Connection } from "src/api/connectApi/types";
import { useWorkspace } from "src/api/queries";
import { FieldErrors } from "src/api/types";
import {
  EditorAccordionItem as AccordionItem,
  accordionRootClassName,
} from "src/base-components/EditorAccordionItem";
import { LoadingView } from "src/base-components/LoadingView";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import {
  DatabaseConnectionNode,
  DatabaseConnectionNodeData,
} from "src/constants/NodeDataTypes";
import { ConfigPane } from "src/databaseConnectionNode/editorComponents/ConfigPane";
import { QueryEditor } from "src/databaseConnectionNode/editorComponents/QueryEditor";
import { ResponseMapping } from "src/databaseConnectionNode/editorComponents/ResponseMapping";
import { VariablesMapping } from "src/databaseConnectionNode/editorComponents/VariablesMapping";
import { DatabaseConnectionForm } from "src/databaseConnectionNode/types";
import { useFormApplyNodeSuggestion } from "src/nodeEditor/applyNodeSuggestion";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

type WrapperProps = {
  node: DatabaseConnectionNode;
  workspaceId: string;
} & NodeEditorBaseProps<DatabaseConnectionNodeData>;

export const DatabaseConnecionNodeEditor: React.FC<WrapperProps> = ({
  immutable,
  node,
  workspaceId,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const workspace = useWorkspace(workspaceId);
  const connection = useConnection({
    baseUrl: workspace.data?.base_url,
    id: node.data.connectionId,
  });
  return (
    <LoadingView
      queryResult={connection}
      renderUpdated={(connection: Connection) => (
        <DatabaseConnectionNodeEditorInternal
          connection={connection}
          displayedError={displayedError}
          immutable={immutable}
          isReactive={isReactive}
          node={node}
          onUpdate={onUpdate}
        />
      )}
    />
  );
};

type Props = {
  connection: Connection;
  immutable: boolean;
  node: DatabaseConnectionNode;
} & NodeEditorBaseProps<DatabaseConnectionNodeData>;

const DatabaseConnectionNodeEditorInternal: React.FC<Props> = ({
  connection,
  immutable,
  node,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const currentFormValues: DatabaseConnectionForm = {
    variables: node.data.variables,
    query: node.data.query,
    response: node.data.response,
    config: node.data.config,
  };

  const formProps = useForm<DatabaseConnectionForm>({
    defaultValues: currentFormValues,
    ...(isReactive && { values: currentFormValues }),
  });

  useFormApplyNodeSuggestion<DatabaseConnectionForm>(node.id, (suggestion) => {
    if (suggestion.variables) {
      formProps.setValue("variables", suggestion.variables);
    }
    if (suggestion.query) {
      formProps.setValue("query", suggestion.query);
    }
    if (suggestion.response) {
      formProps.setValue("response", suggestion.response);
    }
    if (suggestion.config) {
      formProps.setValue("config", suggestion.config);
    }
  });

  useSubmitForm({
    onChange: (data: DatabaseConnectionForm) => {
      onUpdate({ newData: data });
    },
    disabled: isReactive,
    previousValues: currentFormValues,
    watch: formProps.watch,
  });

  const runFieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors)
    : undefined;

  const isSQLProvider = node.data.providerResource.provider != "mongodb";

  return (
    <FormProvider {...formProps}>
      <form>
        <Root
          className={accordionRootClassName}
          defaultValue={["variables", "query", "response", "config"]}
          type="multiple"
        >
          <AccordionItem
            className="pr-6 pb-6"
            disabled={immutable || renderedInDiffView}
            title="Variables"
            value="variables"
          >
            <VariablesMapping
              immutable={immutable}
              provider={node.data.providerResource.provider}
              runFieldErrors={runFieldErrors}
            />
          </AccordionItem>
          <AccordionItem
            className="pr-6 pb-6"
            disabled={immutable || renderedInDiffView}
            title={isSQLProvider ? "SQL query" : "Query"}
            value="query"
          >
            <QueryEditor
              displayedError={displayedError}
              immutable={immutable}
              isReactive={isReactive}
              isSQLProvider={isSQLProvider}
            />
          </AccordionItem>
          <AccordionItem
            className="pr-6 pb-6"
            disabled={immutable || renderedInDiffView}
            title={
              isSQLProvider ? "Configure SQL response" : "Configure response"
            }
            value="response"
          >
            <ResponseMapping
              immutable={immutable}
              runFieldErrors={runFieldErrors}
            />
          </AccordionItem>
          <AccordionItem
            className="pr-6 pb-6"
            disabled={immutable || renderedInDiffView}
            title="Advanced settings"
            value="config"
          >
            <ConfigPane connection={connection} immutable={immutable} />
          </AccordionItem>
        </Root>
      </form>
    </FormProvider>
  );
};
